import React from "react";
import { Link } from "react-router-dom";
import { PageIntro } from "../components/PageIntro";
import { ProblemList } from "../components/ProblemList";
import { ClosingCTA } from "../components/ClosingCTA";
import { PhotoInterlude } from "../components/PhotoInterlude";
import { SectionLabel } from "../components/SectionLabel";
import { OperationsHealth } from "../components/fragments/OperationsHealth";
import { payrollCategories } from "../data/payroll";
import { photos } from "../data/photography";
export function PayrollOperations() {
  return <>
      <PageIntro label="Payroll Operations" title="Payroll that runs correctly. Every cycle." image={photos.payrollMaterials} imageCaption="The calendar is set. The checks are known. Payroll is handled." data-id="element-816" data-mp="14boi96d03x62">
        <p className="mt-10 max-w-2xl text-lg leading-relaxed text-charcoal/80 md:text-xl" data-id="element-817" data-mp="3a3v7yfx473qs">
          We run payroll, fix it when it breaks, cover it when people leave, improve how it works, and lead it when you
          need senior judgment.
        </p>
      </PageIntro>

      <section aria-label="Payroll capabilities" className="shell pb-24 md:pb-32" data-id="element-818" data-mp="3lrztmjipunvc">
        <ol className="border-t border-navy" data-id="element-819" data-mp="3hq5x8nfk060z">
          {payrollCategories.map(c => <li key={c.verb} className="grid grid-cols-12 gap-x-6 gap-y-6 border-b border-stone py-12 md:py-16" data-id="element-820" data-mp="3hns3chwm0zv0">
              <span className="col-span-12 font-display text-lg tabular-nums text-oxblood md:col-span-1" data-id="element-821" data-mp="dbxya78pbx0g">{c.number}</span>
              <div className="col-span-12 md:col-span-5" data-id="element-822" data-mp="9a319ext05sh">
                <h2 className="font-display text-[clamp(2.5rem,4.6vw,4.25rem)] leading-none text-navy" data-id="element-823" data-mp="98i6bo7kuku3">
                  <span className="text-oxblood" data-id="element-824" data-mp="4d2vl54v951w">{c.verb}</span> payroll
                </h2>
                <p className="mt-6 max-w-sm text-lg leading-relaxed text-charcoal/80" data-id="element-825" data-mp="3vjnkai7g80vr">{c.line}</p>
              </div>
              <ul className="col-span-12 self-start border-b border-stone md:col-span-5 md:col-start-8" data-id="element-826" data-mp="2k2dznscl32sv">
                {c.items.map(item => <li key={item} className="border-t border-stone py-3 text-[15px] text-charcoal" data-id="element-827" data-mp="2lkuqnufxtddq">
                    {item}
                  </li>)}
              </ul>
            </li>)}
        </ol>
      </section>

      <section aria-labelledby="measured-title" className="mb-24 bg-stone/40 md:mb-32" data-id="element-828" data-mp="2cl7rlfaokjnu">
        <div className="shell grid grid-cols-12 items-center gap-x-6 gap-y-12 py-24 md:py-32" data-id="element-829" data-mp="2hk2easbrqsd8">
          <div className="col-span-12 lg:col-span-5" data-id="element-830" data-mp="22qyz2rlngqpo">
            <SectionLabel data-id="element-831" data-mp="27ktqkh7hy9zb">Measured</SectionLabel>
            <h2 id="measured-title" className="mt-6 font-display text-[clamp(2.25rem,4.2vw,3.5rem)] leading-[1.06] text-navy" data-id="element-832" data-mp="27lmqay81fz7y">
              “Handled” is something we can show you.
            </h2>
            <p className="mt-6 max-w-md text-lg leading-relaxed text-charcoal/80" data-id="element-833" data-mp="1xrmd092lrno5">
              Every engagement is tracked against the same few numbers — accuracy, off-cycle runs, on-time approvals,
              and manual corrections. You see them every cycle.
            </p>
          </div>
          <div className="col-span-12 lg:col-span-6 lg:col-start-7" data-id="element-834" data-mp="159lc5gzowlrv">
            <OperationsHealth data-id="element-835" data-mp="109pd7w5unkj6" />
          </div>
        </div>
      </section>

      <PhotoInterlude image={photos.corridor} label="Continuity" statement="Same steps. Same checks. Every cycle — whoever is in the office." align="right" data-id="element-836" data-mp="1f5ybaue07ru0" />

      <section aria-labelledby="payroll-systems" className="grid grid-cols-1 lg:grid-cols-2" data-id="element-837" data-mp="1o5l2knd0rh9c">
        <div className="bg-navy px-6 py-20 text-ivory md:px-10 md:py-28 lg:px-16" data-id="element-838" data-mp="1j81r1v7phil3">
          <div className="ml-auto max-w-[560px]" data-id="element-839" data-mp="1xvwjdahsxkxn">
            <p className="eyebrow text-ivory/75" data-id="element-840" data-mp="1xvmuxrcenlu2">Payroll + systems</p>
            <h2 id="payroll-systems" className="mt-8 font-display text-[clamp(2rem,3.6vw,3.25rem)] leading-[1.08]" data-id="element-841" data-mp="1swth1xt7hstd">
              Payroll is only as good as the system it runs on.
            </h2>
          </div>
        </div>
        <div className="bg-stone/50 px-6 py-20 md:px-10 md:py-28 lg:px-16" data-id="element-842" data-mp="22wu2pqejsl0q">
          <div className="max-w-[520px]" data-id="element-843" data-mp="7kdnhsgu5se">
            <p className="text-lg leading-relaxed text-charcoal/85" data-id="element-844" data-mp="6hmd8r8e1by">
              When the problem is the system — a wrong setting, a broken integration, an implementation that never
              finished — our HR technology team works in the same engagement. One firm, both sides of the problem.
            </p>
            <Link to="/hr-technology" className="link-rule mt-10" data-id="element-845" data-mp="3df3x3hjen2qu">
              HR Technology
            </Link>
          </div>
        </div>
      </section>

      <ProblemList area="payroll" title="Payroll problems we’re called for." data-id="element-846" data-mp="3ieqlmn4oo5ey" />
      <ClosingCTA data-id="element-847" data-mp="3nc8llo0q3aat" />
    </>;
}